/**
 * Department goals and the department score (docs/plans/scores.md S2, Lj 2026-10-09/10): each department has 3–5
 * goals — a metric, a target, its own period (day / week / month), up or down — and its score is how it's doing
 * against them. Counts are judged on PACE (done ÷ what should be done by now), so Monday morning isn't a zero and a
 * week goal mixes fairly with a month goal; rates and levels (earned per hour, overdue AR) are judged against the
 * target as they stand. Replaces deptscore.js's 30-day-baseline mark on the desk (the baseline stays as context).
 * Pure — no I/O.
 *
 * A goal: { id, key, target, period: day|week|month, direction?: up|down, taskId?, label? } on the department doc
 * (`goals`). Keys:
 *   onEstimate · productiveShare · earnedPerHour · tasksDone · hoursWorked   the department's own work (fieldgoals.js)
 *   photosTaken · setupCleanup · jobMargin   Field's library (fieldgoals.js — Field builds from FIELD_PRESETS only)
 *   tag:<slug>        tasks with that tag finished in the period (any department): #assessment, #content, #bug…
 *   open:<slug>       open tasks with that tag, as they stand (any department): open:bug, open:deploy (Dev's set)
 *   metric:<id>       a business metric's values (Phase 4, `metrics`) — tap +1 on the goal tile logs one
 *   personal:<id>     a personal counter (Personal only; private)
 *   cfo:arOverdue · cfo:runwayWeeks · cfo:filingsLate   the books' numbers as they stand (the CFO view)
 *   ops:receiptsNoJob · ops:oldestOpenDays · ops:openTasks   levels read off the device
 *   sales:leads · sales:estimatesSent · sales:won · sales:wonValue · sales:winRate
 *   finance:collected · finance:invoicesSent · finance:paidWithin14Pct · finance:bankRecStale · finance:receiptsStale ·
 *   finance:filingsOnTimePct · finance:budgetsOnTrackPct   the books' Sales / Finance feed (booksfeed.js, S4: dated
 *                     rows counted over the goal's window) — null until the feed reaches the device ("waiting")
 *   task:<taskId>     one task as the goal (taskgoals.js, B6): `measure` hours · subtasks · progress · review · doneBy,
 *                     period week / month / until <due> — closes itself when the task is done or removed
 */
import { FIELD_GOAL_METRICS, FIELD_PRESETS, FIELD_COUNTS, payRateOf } from './fieldgoals.js';
import { CALL_GOAL_METRICS, CALL_PRESETS, CALL_COUNTS } from './callsmetrics.js';   // calls:made · calls:customers (Sales)
import { BOOKS_GOAL_METRICS, BOOKS_COUNTS, SALES_PRESETS, FINANCE_PRESETS } from './booksfeed.js';   // sales:* · finance:* (S4)
import { workDepartments } from './workdept.js';
import { departmentOf } from './hierarchy.js';
import { addDaysStr } from './phonestats.js';
import { localDateStr } from './time.js';
import { canonTag } from './tags.js';
import { valuesFor } from './metrics.js';
import { isTaskGoal, taskGoalMetric, taskGoalValue, taskGoalClosed, taskGoalSuggest, taskGoalTaskId, cleanTaskGoalFields, untilWindow, dueDateText } from './taskgoals.js';   // task:<id> goals (B6)

const DAY = 86400000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const round1 = (v) => Math.round(v * 10) / 10;

export const GOAL_PERIODS = ['day', 'week', 'month'];
export const GOAL_PERIOD_LABEL = { day: 'Day', week: 'Week', month: 'Month', until: 'Until a date' };

/**
 * The menu. kind: count (adds up over the period — judged on pace) · rate / level (as it stands — judged against the
 * target). direction: up = more is better, down = a limit. unit: '' | '%' | '$' | '$/h' | 'h' | 'd' | 'wks'.
 */
export const DEPT_GOAL_METRICS = Object.freeze({
  onEstimate: { label: 'Efficiency', unit: '%', kind: 'rate', direction: 'up', hint: 'estimated ÷ actual hours on tasks finished — 100% = on the estimate, over 100% = faster' },
  productiveShare: { label: 'Productive share', unit: '%', kind: 'rate', direction: 'up', hint: 'hours on this department’s work (setup, cleanup, runs and travel included) ÷ every hour you worked — the rest is other work: invoices, admin, other departments' },
  earnedPerHour: { label: 'Earned per hour', unit: '$/h', kind: 'rate', direction: 'up', hint: 'fixed / product jobs: the labour in the price × share of the estimate finished; T&M jobs: hours × their bill rate — ÷ every hour on site. Each job has its own target by its type' },
  tasksDone: { label: 'Tasks finished', unit: '', kind: 'count', direction: 'up', hint: 'tasks finished in this department (not checklists)' },
  hoursWorked: { label: 'Hours worked', unit: 'h', kind: 'count', direction: 'up', hint: 'hours on this department’s tasks' },
  'cfo:arOverdue': { label: 'Overdue receivables', unit: '$', kind: 'level', direction: 'down', hint: 'invoices past due, from the books' },
  'cfo:runwayWeeks': { label: 'Cash runway', unit: 'wks', kind: 'level', direction: 'up', hint: 'bank ÷ average weekly spend, from the books' },
  'cfo:filingsLate': { label: 'Filings late', unit: '', kind: 'level', direction: 'down', hint: 'GST, payroll and instalments past their due date' },
  'cfo:collectedPct': { label: 'Money collected', unit: '%', kind: 'rate', direction: 'up', hint: 'the share of what customers owe that isn’t overdue, from the books' },
  'cfo:filingsOnTimePct': { label: 'Filings on time', unit: '%', kind: 'rate', direction: 'up', hint: 'the share of filings due in the 90-day window that aren’t late (GST, payroll, instalments)' },
  'cfo:budgetsOnTrackPct': { label: 'Budgets on track', unit: '%', kind: 'rate', direction: 'up', hint: 'the share of this period’s budgets not over their limit or heading over it (CFO budgets)' },
  'ops:booksCurrentPct': { label: 'Books current', unit: '%', kind: 'rate', direction: 'up', hint: '100% with no receipts waiting for a job, 20 points off for each one' },
  'ops:storeRuns': { label: 'Store runs', unit: '', kind: 'count', direction: 'down', hint: 'material runs (the Material run quick task, tasks tagged #materials) — fewer is better: plan the materials ahead' },
  'ops:receiptsNoJob': { label: 'Receipts waiting', unit: '', kind: 'level', direction: 'down', hint: 'receipts with a line on no job' },
  'ops:oldestOpenDays': { label: 'Oldest open task', unit: 'd', kind: 'level', direction: 'down', hint: 'days since the oldest open task here was made' },
  'ops:openTasks': { label: 'Open tasks', unit: '', kind: 'level', direction: 'down', hint: 'open tasks in this department' },
  ...BOOKS_GOAL_METRICS,
  ...FIELD_GOAL_METRICS,
  ...CALL_GOAL_METRICS,
});

const TAG_LABEL = { assessment: 'Assessments done', content: 'Content published', bug: 'Bugs closed', shipped: 'Shipped to live', improvement: 'Improvements', ad: 'Ads posted', flier: 'Fliers posted', campaign: 'Campaigns launched', callback: 'Callbacks' };
const OPEN_LABEL = { bug: 'Open bugs', deploy: 'Waiting to deploy' };
const OPEN_HINT = { bug: 'open tasks tagged #bug (any department), as they stand — a limit', deploy: 'open tasks tagged #deploy — built, not live yet (any department), as they stand — a limit' };

/**
 * Dev's fixed library (department-plans.md "Dev — the BattleSuite shipping", Lj Oct 11: "for Dev, goals will mostly be
 * task completions / feature rollouts"). Dev builds from these only, like Field. "Built → live days" waits on a deploy
 * log (when a task was tagged #deploy isn't kept).
 */
export const DEV_PRESETS = Object.freeze([
  { key: 'tag:shipped', period: 'week', target: 3 },
  { key: 'tag:bug', period: 'week', target: 5 },
  { key: 'open:bug', period: 'week', target: 5, direction: 'down' },
  { key: 'open:deploy', period: 'week', target: 0, direction: 'down' },
  { key: 'hoursWorked', period: 'week', target: 20 },
  { key: 'tag:improvement', period: 'week', target: 3 },
]);

/** The metric a key names: { label, unit, kind, direction, hint } (tag / metric / personal keys built on the fly). */
export function goalMetric(key, { metrics = [], personalMetrics = [], goal = null, tasks = [] } = {}) {
  if (DEPT_GOAL_METRICS[key]) return DEPT_GOAL_METRICS[key];
  const [kind, ...rest] = String(key || '').split(':');
  const ref = rest.join(':');
  if (kind === 'task' && ref) return taskGoalMetric(goal?.key === key ? goal : { key }, { tasks });   // the goal's measure, the task's title
  if (kind === 'tag' && ref) return { label: TAG_LABEL[ref] || `#${ref} done`, unit: '', kind: 'count', direction: ref === 'callback' ? 'down' : 'up', hint: `tasks tagged #${ref} finished` };
  if (kind === 'open' && ref) return { label: OPEN_LABEL[ref] || `Open #${ref}`, unit: '', kind: 'level', direction: 'down', hint: OPEN_HINT[ref] || `open tasks tagged #${ref} (any department), as they stand` };
  if (kind === 'metric' || kind === 'personal') {
    const def = (kind === 'metric' ? metrics : personalMetrics).find((d) => d.id === ref);
    const unit = def?.unit === '$' || def?.kind === 'money' ? '$' : def?.unit || '';
    return { label: def?.name || 'A counter', unit, kind: def?.agg === 'latest' ? 'level' : 'count', direction: def?.goal?.direction === 'down' ? 'down' : 'up', hint: kind === 'personal' ? 'your counter — private' : 'tap +1 on the tile, or a number when a linked task is done', tappable: !!def && def.source !== 'auto' };
  }
  return null;
}

// ── the department's identity ─────────────────────────────────────────────────────────────────────────────────

/** Which of the known departments this is (by id / name): sales · marketing · finance · dev · field · general · personal · null. */
export function deptRole(dept) {
  if (!dept) return null;
  if (dept.id === 'general') return 'general';
  const n = String(dept.name || '').trim().toLowerCase();
  for (const r of ['sales', 'marketing', 'finance', 'dev', 'field', 'general', 'personal']) if (n === r) return r;
  if (/^(office|admin|accounting)/.test(n)) return 'finance';
  if (/^(development|software|build the app)/.test(n)) return 'dev';
  return null;
}

/** Each department's colour (the dashboards, chips): its own, else the role's. */
/**
 * The neon swatches a department can wear (Lj, Oct 10: "rgb neon", "colors need work", "should be able to pick
 * colors", "battle orange isn't even an option"). The brand's two first, then the hues; each is one colour family — dark →
 * the colour → light (given, or worked out by neonTriple) — so a ring, a line or a bar glows in one hue. Any #hex works.
 */
export const NEON_SWATCHES = Object.freeze([
  { name: 'Battle orange', color: '#FB5446', neon: ['#E23A2C', '#FB5446', '#FF8F70'], brand: true },
  { name: 'Battle gold', color: '#FFD23F', neon: ['#FFB000', '#FFD23F', '#FFEA8A'], brand: true },
  { name: 'Hot red', color: '#FF2E63', neon: ['#FF1F4B', '#FF2E63', '#FF8A5C'] },
  { name: 'Crimson', color: '#E0115F' },
  { name: 'Ember', color: '#FF6A1F', neon: ['#FF4B1F', '#FF6A1F', '#FFB23D'] },
  { name: 'Tangerine', color: '#FF8C1A' },
  { name: 'Amber', color: '#FFB300' },
  { name: 'Sun', color: '#FFE600' },
  { name: 'Lime', color: '#B6FF2E', neon: ['#5CFF3D', '#B6FF2E', '#F0FF6B'] },
  { name: 'Neon green', color: '#39FF14' },
  { name: 'Mint', color: '#00F5A0', neon: ['#00D97E', '#00F5A0', '#8CFFD9'] },
  { name: 'Teal', color: '#00D1B2' },
  { name: 'Cyan', color: '#00E1FF', neon: ['#00A8FF', '#00E1FF', '#9BF6FF'] },
  { name: 'Sky', color: '#38BDF8' },
  { name: 'Electric blue', color: '#3D7BFF', neon: ['#2E4BFF', '#3D7BFF', '#7CC4FF'] },
  { name: 'Indigo', color: '#5B5BFF' },
  { name: 'Violet', color: '#9D5CFF', neon: ['#6E3DFF', '#9D5CFF', '#D7A3FF'] },
  { name: 'Purple', color: '#C04DFF' },
  { name: 'Magenta', color: '#FF3DD8', neon: ['#C23DFF', '#FF3DD8', '#FF9CE5'] },
  { name: 'Pink', color: '#FF5CA8' },
  { name: 'Rose', color: '#FF7A9A' },
  { name: 'Copper', color: '#D97A4A' },
  { name: 'Steel', color: '#9FB6CC', neon: ['#6F8BA8', '#9FB6CC', '#DCE8F3'] },
  { name: 'Silver', color: '#D9D4CC' },
]);

export const ROLE_COLORS = Object.freeze({ sales: '#FF2E63', marketing: '#FFC21A', finance: '#00F5A0', dev: '#9D5CFF', field: '#B6FF2E', general: '#9FB6CC', personal: '#00E1FF' });
export const deptColor = (dept) => dept?.color || ROLE_COLORS[deptRole(dept)] || '#FF2E63';

const hexToHsl = (hex) => {
  const m = String(hex || '').replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return null;
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x, 16) / 255);
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min; const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, sat, l];
};
const hslToHex = (h, sat, l) => {
  const k = (n) => (n + h / 30) % 12; const a = sat * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${[f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};

/** A colour's neon family [deep, the colour, light]: its swatch's, else worked out (hue −12° deeper, +14° lighter). */
export function neonTriple(hex) {
  const sw = NEON_SWATCHES.find((x) => x.color.toLowerCase() === String(hex || '').toLowerCase());
  if (sw?.neon) return [...sw.neon];
  const hsl = hexToHsl(hex);
  if (!hsl) return [...NEON_SWATCHES[0].neon];
  const [h, sat, l] = hsl;
  return [hslToHex((h + 348) % 360, Math.min(1, sat * 1.05), Math.max(0.35, l - 0.08)), String(hex).toUpperCase(), hslToHex((h + 14) % 360, sat, Math.min(0.85, l + 0.16))];
}

/** A department's neon family (from its colour). */
export const deptNeon = (dept) => neonTriple(deptColor(dept));

/** Starting goals per role — metric picks with no target yet (the tile asks for one). Lj's Oct 10 list. */
export const ROLE_GOALS = Object.freeze({
  sales: [...SALES_PRESETS, { key: 'metric:outreach', period: 'day' }, ...CALL_PRESETS],
  marketing: [{ key: 'sales:leads', period: 'month' }, { key: 'tag:content', period: 'week' }, { key: 'metric:audience', period: 'month' }],
  finance: [...FINANCE_PRESETS, { key: 'finance:budgetsOnTrackPct', period: 'month', target: 100 }, { key: 'ops:booksCurrentPct', period: 'week', target: 100 }],
  dev: DEV_PRESETS,
  field: FIELD_PRESETS,
  general: [{ key: 'hoursWorked', period: 'week', direction: 'down' }, { key: 'tasksDone', period: 'week' }, { key: 'ops:oldestOpenDays', period: 'week', target: 30 }],
  personal: [],
});

/** The business metrics the starter goals count on (manual, tap +1). Ids are fixed so the goals find them. */
export const DEPT_STARTER_METRICS = Object.freeze([
  { id: 'outreach', name: 'Outreach touches', unit: '', kind: 'count', agg: 'sum', source: 'manual', attachTo: ['person', 'org'], tags: ['sales'] },
  { id: 'audience', name: 'Email list sign-ups', unit: '', kind: 'count', agg: 'sum', source: 'manual', attachTo: ['org'], tags: ['marketing'] },
]);

/** The four departments Lj added on Oct 10. Non-billable (overhead work), feeding the books like the rest. */
export const STARTER_DEPARTMENTS = Object.freeze([
  { name: 'Sales', icon: '🎯', color: ROLE_COLORS.sales, billable: false },
  { name: 'Marketing', icon: '📣', color: ROLE_COLORS.marketing, billable: false },
  { name: 'Finance', icon: '💵', color: ROLE_COLORS.finance, billable: false },
  { name: 'Dev', icon: '💻', color: ROLE_COLORS.dev, billable: false },
]);

/** Starter departments not there yet (by name, any case). */
export const missingStarterDepartments = (departments = []) => {
  const have = new Set(departments.map((d) => String(d.name || '').trim().toLowerCase()));
  return STARTER_DEPARTMENTS.filter((d) => !have.has(d.name.toLowerCase()));
};

let seq = 0;
const gid = () => `g${Date.now().toString(36)}${(seq++).toString(36)}`;

/** A clean goal (unknown period → week; target a number or null; direction only when it overrides). */
export function cleanGoal(g, ctx = {}) {
  if (!g || !g.key || !goalMetric(g.key, ctx) && !/^(tag|metric|personal):./.test(g.key)) return null;
  const out = { id: g.id || gid(), key: String(g.key), target: num(g.target), period: GOAL_PERIODS.includes(g.period) ? g.period : 'week' };
  if (g.direction === 'up' || g.direction === 'down') out.direction = g.direction;
  if (g.taskId) out.taskId = g.taskId;
  if (g.star) out.star = true;
  const days = Array.isArray(g.days) ? [...new Set(g.days.map(Number).filter((x) => x >= 0 && x <= 6))].sort() : [];
  if (days.length && days.length < 7) out.days = days;   // the days it counts on (none = every day)
  if (g.when === 'clocked') { out.when = 'clocked'; delete out.days; }   // only days you're clocked into this department   // one of the department's headline three (Lj: "the most important 3 stand out")
  // a target that follows a rate instead of a fixed number — "my hourly rate from BattleBooks × pct" (Lj, Oct 10)
  if (g.targetFrom && g.targetFrom.kind === 'payRate') out.targetFrom = { kind: 'payRate', pct: num(g.targetFrom.pct) ?? 100 };
  // Earned per hour by job type (v0.1.258): the hours-weighted blend of the jobs' own targets (fieldgoals.js jobTarget)
  else if (g.targetFrom && g.targetFrom.kind === 'jobs' && g.key === 'earnedPerHour') out.targetFrom = { kind: 'jobs' };
  if (g.label) out.label = String(g.label).slice(0, 60);
  if (isTaskGoal(out)) cleanTaskGoalFields(g, out);   // measure, until <due>, closed — taskgoals.js
  return out;
}

/**
 * The department's goals: its own (`dept.goals`). Personal: your counters that have a goal.
 * → [{ id, key, target, period, direction, taskId?, label?, starter? }]
 */
export function goalsFor(dept, { personalMetrics = [] } = {}) {
  const role = deptRole(dept);
  if (role === 'personal') {
    return personalMetrics.filter((d) => d && d.goal && num(d.goal.value) != null && !d.archived).map((d) => ({
      id: `p_${d.id}`, key: `personal:${d.id}`, target: num(d.goal.value), period: GOAL_PERIODS.includes(d.goal.period) ? d.goal.period : 'day', direction: d.goal.direction === 'down' ? 'down' : 'up',
    }));
  }
  // Only the goals the department has (Lj, Oct 10: no default goals — "a bunch of data for goals that don't exist
  // yet"). ROLE_GOALS are suggestions in the goal menu, not goals.
  return Array.isArray(dept?.goals) ? dept.goals.map((g) => cleanGoal(g)).filter(Boolean) : [];
}

/** The role's suggested goals for the goal menu (not goals until built). */
export const suggestedGoals = (dept) => (ROLE_GOALS[deptRole(dept)] || ROLE_GOALS.general).map((g) => ({ ...g }));

/**
 * Departments that build goals from a fixed library only (Lj, Oct 11: Field — "hard-coded flexibility: they pick
 * what's important, but the data must be accurate"; Dev the same day): no free-form metrics or new counters.
 * → the presets, or null.
 */
export const fixedPresets = (dept) => {
  const role = deptRole(dept);
  const set = role === 'field' ? FIELD_PRESETS : role === 'dev' ? DEV_PRESETS : null;
  return set ? set.map((g) => ({ ...g })) : null;
};
/** May this department build a goal on `key`? (Field and Dev: their presets only — and task goals, which count the task's own data.) */
export const goalKeyAllowed = (dept, key) => { const f = fixedPresets(dept); return !f || f.some((g) => g.key === key) || /^task:./.test(String(key || '')); };

/** A goal's window: its period's (goalWindow), or start → due for a task goal's "until <date>" (pace linear to it). */
export const goalWindowFor = (g, now = Date.now(), back = 0, worked = null) => (g?.period === 'until' && isTaskGoal(g) ? untilWindow(g, now) : goalWindow(g?.period, now, back, g?.days, worked));

// ── periods ─────────────────────────────────────────────────────────────────────────────────────────────────

const WORK_START = 6; const WORK_END = 20;   // the day's share runs 6 am → 8 pm
const dayShare = (now) => { const d = new Date(now); const h = d.getHours() + d.getMinutes() / 60; return Math.max(0, Math.min(1, (h - WORK_START) / (WORK_END - WORK_START))); };

/**
 * The period holding `now` (or `back` periods before it): { period, from, to, end, share } — dates inclusive,
 * YYYY-MM-DD; `to` = today for the current one (nothing after now counts), `end` = the period's last day; share = how
 * much of it has gone (1 for a past one). Weeks start Monday; months are calendar months.
 */
export const isGoalDay = (date, days) => !Array.isArray(days) || !days.length || days.includes(new Date(`${date}T12:00:00`).getDay());

/**
 * The dates you worked in a department (its tasks' time entries), plus today while you're clocked in there — for goals
 * that count "when I'm clocked into this department" (Lj, Oct 10). → Set of YYYY-MM-DD
 */
export function workedDates(dept, data = {}) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const out = new Set();
  for (const t of tasks) for (const e of t.timeEntries || []) if (e.date && (e.duration || 0) > 0 && wd.forEntry(e, t)?.id === dept?.id) out.add(e.date);
  const c = data.clock;
  if (c?.active) {
    const running = c.taskId ? tasks.find((t) => t.id === c.taskId) : null;
    const here = running ? wd.forTask(running)?.id === dept?.id : c.projectId ? departmentOf(projects.find((p) => p.id === c.projectId), departments)?.id === dept?.id : false;
    if (here) out.add(localDateStr(data.now || Date.now()));
  }
  return out;
}

/** Which days a goal counts on, as words: "every day" · "weekdays" · "weekends" · "Mon, Wed, Fri". */
export function goalDaysText(days, when = null) {
  if (when === 'clocked') return 'when clocked in here';
  if (!Array.isArray(days) || !days.length || days.length === 7) return 'every day';
  const k = [...days].sort().join(',');
  if (k === '1,2,3,4,5') return 'weekdays';
  if (k === '0,6') return 'weekends';
  const N = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return [1, 2, 3, 4, 5, 6, 0].filter((d) => days.includes(d)).map((d) => N[d]).join(', ');
}

/** A trip for materials: the Material run quick task, a pickup tag, or a pickup in the title (8h 57m of them read as 0 runs). */
const RUN_TAGS = ['materials', 'pickup', 'supplies'];
export const isMaterialRun = (t) => !!t && (t.quickTaskId === 'material-run' || (t.tags || []).some((x) => RUN_TAGS.includes(String(x).toLowerCase())) || /\bmaterials? pick ?up\b|\bstore run\b/i.test(String(t.title || '')));

/**
 * The period holding `now` (or `back` periods before it): { period, from, to, end, share, off } — dates inclusive,
 * YYYY-MM-DD; `to` = today for the current one (nothing after now counts), `end` = the period's last day; share = how
 * much of it has gone (1 for a past one). Weeks start Monday; months are calendar months. `days` (JS weekdays, 0 = Sun)
 * = the days the goal counts on (Lj, Oct 10: "weekdays, custom"): the share runs over those days only, and a day goal
 * on a day it doesn't count is `off`. `worked` (a Set of dates) = "when I'm clocked into this department": only days
 * worked here count; a week / month paces over the days worked so far plus the weekdays still ahead.
 */
export function goalWindow(period, now = Date.now(), back = 0, days = null, worked = null) {
  const today = localDateStr(now);
  const d = new Date(`${today}T12:00:00`);
  if (period === 'day') {
    const day = addDaysStr(today, -back);
    return { period, from: day, to: day, end: day, share: back ? 1 : dayShare(now), off: worked ? !worked.has(day) : !isGoalDay(day, days) };
  }
  let from; let end;
  if (period === 'month') {
    const first = new Date(d.getFullYear(), d.getMonth() - back, 1, 12);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0, 12);
    from = localDateStr(first.getTime()); end = localDateStr(last.getTime());
  } else {
    const dow = (d.getDay() + 6) % 7;   // Monday = 0
    from = addDaysStr(today, -dow - 7 * back); end = addDaysStr(from, 6);
  }
  let total = 0; let gone = 0;
  for (let x = from; x <= end; x = addDaysStr(x, 1)) {
    // "when clocked in here": the days worked here so far count; the weekdays still ahead are the rest of the period
    const counts = worked ? (x < today || back ? worked.has(x) : x === today ? worked.has(x) || isGoalDay(x, [1, 2, 3, 4, 5]) : isGoalDay(x, [1, 2, 3, 4, 5])) : isGoalDay(x, days);
    if (!counts) continue;
    total += 1;
    if (back || x < today) gone += 1;
    else if (x === today) gone += dayShare(now);
  }
  return { period: period === 'month' ? 'month' : 'week', from, to: back ? end : today, end, share: total ? Math.min(1, gone / total) : 1, off: false };
}

const inWin = (date, w) => !!date && date >= w.from && date <= w.to;
const rowDate = (r) => r?.date || (r?.at ? localDateStr(r.at) : null);
const completedDate = (t) => (t?.status === 'done' && t.completedAt ? localDateStr(t.completedAt) : null);
const hasTag = (t, tag) => Array.isArray(t?.tags) && t.tags.some((x) => canonTag(String(x)) === tag);

/** Receipts snapped with a line on no job (the Field desk's signal, counted). */
export function looseReceiptCount(receipts = []) {
  let n = 0;
  for (const r of receipts || []) {
    if (!r || r.status === 'void' || r.status === 'removed') continue;
    const pages = Object.values(r.pages || {}).filter((p) => p && p.status !== 'void');
    if (pages.some((p) => !(p.lines || []).length || (p.lines || []).some((l) => !l.projectId))) n += 1;
  }
  return n;
}

/**
 * A goal's value for a window → { value, rows? } (value null = nothing to measure yet, `feed` = waiting on a feed).
 * data: { tasks, projects, departments, departmentMode, metrics, metricValues, personalMetrics, personalMetricValues,
 *         metricCtx, cfo (the CFO view), receipts, sales + finance (booksfeed.js booksFeed), now }
 */
export function goalValue(goal, dept, w, data = {}) {
  const key = goal.key;
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  if (FIELD_COUNTS[key]) return FIELD_COUNTS[key](dept, w, data);
  if (CALL_COUNTS[key]) return CALL_COUNTS[key](dept, w, data);
  if (BOOKS_COUNTS[key]) return BOOKS_COUNTS[key](dept, w, data);   // the books' Sales / Finance feed (booksfeed.js)
  if (isTaskGoal(goal)) return taskGoalValue(goal, w, data);   // one task as the goal (taskgoals.js)
  const [kind, ...rest] = key.split(':');
  const ref = rest.join(':');
  if (kind === 'tag') {
    const rows = tasks.filter((t) => !t.deletedAt && inWin(completedDate(t), w) && hasTag(t, canonTag(ref)));
    return { value: rows.length, rows: rows.map((t) => ({ taskId: t.id, title: t.title, date: completedDate(t) })), basis: `${rows.length} task${rows.length === 1 ? '' : 's'} tagged #${canonTag(ref)} finished (any department)` };
  }
  if (kind === 'open') {
    // as it stands now (a level): open = not done, not skipped, not deleted — oldest first
    const slug = canonTag(ref);
    const now = data.now || Date.now();
    const rows = tasks.filter((t) => !t.deletedAt && t.status !== 'done' && t.status !== 'skipped' && hasTag(t, slug))
      .sort((a, b) => (num(a.createdAt) ?? Infinity) - (num(b.createdAt) ?? Infinity))
      .map((t) => { const age = num(t.createdAt) ? Math.max(0, Math.floor((now - num(t.createdAt)) / DAY)) : null; return { taskId: t.id, title: t.title, ageDays: age, date: num(t.createdAt) ? localDateStr(num(t.createdAt)) : null }; });
    const noAge = rows.filter((r) => r.ageDays == null).length;
    const notes = [];
    if (rows.length) notes.push('age = days since the task was made (when it was tagged isn’t kept)');
    if (noAge) notes.push(`${noAge} with no made date — counted, not aged`);
    return { value: rows.length, rows, basis: `${rows.length} open task${rows.length === 1 ? '' : 's'} tagged #${slug} (any department)`, notes };
  }
  if (kind === 'metric' || kind === 'personal') {
    const personal = kind === 'personal';
    const def = (personal ? data.personalMetrics : data.metrics)?.find((d) => d.id === ref);
    if (!def) return { value: null, missing: 'metric' };
    const rows = valuesFor(def, { values: (personal ? data.personalMetricValues : data.metricValues) || [], ctx: data.metricCtx || {} }).filter((r) => inWin(rowDate(r), w));
    const vals = rows.map((r) => num(r.value)).filter((v) => v != null);
    if (def.agg === 'latest') return { value: vals.length ? vals[vals.length - 1] : null };
    return { value: round1(vals.reduce((a, b) => a + b, 0)), rows };
  }
  if (kind === 'cfo') {
    const cash = data.cfo?.cash;
    if (!data.cfo?.ready) return { value: null, feed: 'books' };
    if (ref === 'arOverdue') return { value: num(cash?.arOverdue?.value ?? cash?.arOverdue) };
    if (ref === 'runwayWeeks') return { value: cash?.runwayMonths == null ? null : round1(cash.runwayMonths * 4.345) };
    if (ref === 'collectedPct') {
      const ar = num(cash?.ar?.value ?? cash?.ar);
      const over = num(cash?.arOverdue?.value ?? cash?.arOverdue) || 0;
      return { value: ar > 0 ? Math.round(Math.max(0, (ar - over) / ar) * 100) : 100 };
    }
    if (ref === 'budgetsOnTrackPct') {
      const rows = (data.cfo?.budgets || []).map((b) => {
        const pct = num(b.pct) ?? (num(b.budget) > 0 && num(b.actual) != null ? (num(b.actual) / num(b.budget)) * 100 : null);
        return pct == null ? null : { label: b.name || b.label || b.target?.name || 'Budget', pct, heading: num(b.forecastEnd) != null && num(b.budget) > 0 && num(b.forecastEnd) > num(b.budget) };
      }).filter(Boolean);
      if (!rows.length) return { value: null, missing: 'budgets' };
      const ok = rows.filter((r) => r.pct <= 100 && !r.heading).length;
      return { value: Math.round((ok / rows.length) * 100), rows: rows.filter((r) => r.pct > 100 || r.heading).map((r) => ({ label: `${r.label} · ${Math.round(r.pct)}%${r.heading ? ' (heading over)' : ''}` })) };
    }
    if (ref === 'filingsOnTimePct') {
      const today = localDateStr(data.now || Date.now());
      const rows = data.cfo?.compliance?.rows || [];
      if (!rows.length) return { value: 100 };
      const late = rows.filter((r) => r.due && r.due < today && r.status !== 'done');
      return { value: Math.round(((rows.length - late.length) / rows.length) * 100), rows: late };
    }
    if (ref === 'filingsLate') {
      const today = localDateStr(data.now || Date.now());
      const rows = (data.cfo?.compliance?.rows || []).filter((r) => r.due && r.due < today && r.status !== 'done');
      return { value: rows.length, rows };
    }
    return { value: null };
  }
  if (kind === 'ops') {
    if (ref === 'receiptsNoJob') return { value: looseReceiptCount(data.receipts) };
    if (ref === 'storeRuns') {
      // each stretch on a run (2 min or more) is one run; the Material run quick task reuses one task a day per job
      const runs = []; let short = 0;
      for (const t of tasks) {
        if (t.deletedAt || !isMaterialRun(t)) continue;
        for (const e of t.timeEntries || []) {
          if (!inWin(e.date, w) || !((e.duration || 0) > 0)) continue;
          if (e.duration >= 120000) runs.push({ taskId: t.id, title: `${t.title} · ${Math.round(e.duration / 60000)} min`, date: e.date }); else short += 1;
        }
      }
      return { value: runs.length, rows: runs, basis: `${runs.length} run${runs.length === 1 ? '' : 's'}: stretches of 2 min or more on the Material run quick task or a #materials task`, notes: short ? [`${short} stretch${short === 1 ? '' : 'es'} under 2 min left out`] : [] };
    }
    if (ref === 'booksCurrentPct') return { value: Math.max(0, 100 - 20 * looseReceiptCount(data.receipts)) };
    const wd = workDepartments({ tasks, projects, departments, departmentMode });
    const open = tasks.filter((t) => t.status !== 'done' && !t.deletedAt && !t.parentId && !t.bucket && wd.forTask(t)?.id === dept?.id);
    if (ref === 'openTasks') return { value: open.length };
    if (ref === 'oldestOpenDays') {
      const now = data.now || Date.now();
      const ages = open.map((t) => (num(t.createdAt) ? Math.floor((now - num(t.createdAt)) / DAY) : null)).filter((v) => v != null);
      return { value: ages.length ? Math.max(...ages) : 0 };
    }
    return { value: null };
  }
  return { value: null };
}

export const STATUS_LABEL = Object.freeze({ closed: 'DONE', off: 'DAY OFF', met: 'MET', ahead: 'AHEAD', on_pace: 'ON PACE', behind: 'BEHIND', close: 'CLOSE', under: 'UNDER', over: 'OVER', none: 'NO DATA', unset: 'SET A GOAL', feed: 'WAITING' });

/**
 * Where a goal stands: { target, value, expected, mark (0–100 | null), status, gap, pct }.
 * count up: pace — expected = target × share; mark = done ÷ expected (100 once met). count down (a limit): 100 while
 * under the limit. rate / level: against the target as it stands.
 */
export function goalPace({ target, value, kind = 'count', direction = 'up', share = 1 }) {
  const t = num(target);
  const v = num(value);
  if (t == null) return { target: null, value: v, expected: null, mark: null, status: 'unset', gap: null, pct: null };
  if (v == null) return { target: t, value: null, expected: null, mark: null, status: 'none', gap: null, pct: null };
  const pct = t ? Math.round((v / t) * 100) : null;
  if (kind === 'count' && direction === 'up') {
    const expected = t * share;
    if (v >= t) return { target: t, value: v, expected, mark: 100, status: 'met', gap: 0, pct };
    const mark = expected <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((100 * v) / expected)));
    const r = expected <= 0 ? (v > 0 ? 2 : 1) : v / expected;
    const status = r >= 1.05 ? 'ahead' : r >= 0.9 ? 'on_pace' : 'behind';
    return { target: t, value: v, expected, mark, status, gap: Math.round((v - expected) * 10) / 10, pct };
  }
  if (kind === 'count' && direction === 'down') {
    const allowed = t * share;
    if (v > t) return { target: t, value: v, expected: allowed, mark: Math.max(0, Math.round((100 * t) / v)), status: 'over', gap: round1(v - t), pct };
    return { target: t, value: v, expected: allowed, mark: v > allowed * 1.1 && share < 1 ? 85 : 100, status: v > allowed * 1.1 && share < 1 ? 'behind' : 'under', gap: round1(t - v), pct };
  }
  if (direction === 'down') {
    if (v <= t) return { target: t, value: v, expected: null, mark: 100, status: 'under', gap: round1(t - v), pct };
    return { target: t, value: v, expected: null, mark: v <= 0 ? 100 : Math.max(0, Math.round((100 * Math.max(t, 0.0001)) / v)), status: 'over', gap: round1(v - t), pct };
  }
  if (v >= t) return { target: t, value: v, expected: null, mark: 100, status: 'met', gap: 0, pct };
  return { target: t, value: v, expected: null, mark: t > 0 ? Math.max(0, Math.round((100 * v) / t)) : 100, status: v >= 0.9 * t ? 'close' : 'behind', gap: round1(v - t), pct };
}

/** The headline three: the starred goals (up to 3), else the first three. → { top, rest } */
export function headlineGoals(goals = []) {
  const starred = goals.filter((g) => g.star).slice(0, 3);
  const top = starred.length ? starred : goals.slice(0, 3);
  const ids = new Set(top.map((g) => g.id));
  return { top, rest: goals.filter((g) => !ids.has(g.id)) };
}

/** The score's word: ≥ 90 ahead, ≥ 70 on pace, else behind. */
export const scoreStatus = (score) => (score == null ? null : score >= 90 ? 'ahead' : score >= 70 ? 'on_pace' : 'behind');

/** Your hourly rate from BattleBooks (opsPay): what you're paid an hour, or your shadow rate when you're unpaid. */
export const myPayRate = (data = {}) => payRateOf(data);

/**
 * A goal's target now: its number, its rate link worked out (null when the rate isn't set), or — Earned per hour on
 * "from your jobs" — the blend of the jobs' own targets the count worked out (`got.jobTarget`, fieldgoals.js; the
 * goal's own number while no job has one).
 */
export function resolveTarget(g, data = {}, got = null) {
  if (g?.targetFrom?.kind === 'payRate') {
    const rate = myPayRate(data);
    // no rate in BattleBooks yet: the goal's own number stands in (Field's Earned per hour: $90/h), else it waits
    return rate == null ? num(g.target) : Math.round(rate * ((num(g.targetFrom.pct) ?? 100) / 100) * 100) / 100;
  }
  if (g?.targetFrom?.kind === 'jobs') return num(got?.jobTarget?.value) ?? num(g.target);
  return num(g?.target);
}

/** Where a goal's target came from: 'rate' · 'fallback' (rate link, no rate yet) · 'jobs' · 'jobsFallback' · 'number'. */
export function targetSourceOf(g, data = {}, got = null) {
  if (g?.targetFrom?.kind === 'payRate') return myPayRate(data) == null ? (num(g.target) == null ? null : 'fallback') : 'rate';
  if (g?.targetFrom?.kind === 'jobs') return num(got?.jobTarget?.value) != null ? 'jobs' : num(g.target) == null ? null : 'jobsFallback';
  return 'number';
}

/**
 * The department's view: every goal with its metric, value and progress for the current period, the score (mean of
 * the marks that have one) and its word. `back` > 0 looks at an earlier period of each goal (the breakdown's history).
 * → { departmentId, role, color, goals: [...], score, status, unset }
 */
export function deptGoalsView(dept, data = {}, { now = data.now || Date.now(), back = 0 } = {}) {
  const ctx = { metrics: data.metrics || [], personalMetrics: data.personalMetrics || [] };
  let worked = null;   // worked here, for "when clocked in" goals — worked out once
  const all = goalsFor(dept, ctx).map((g) => {
    const metric = goalMetric(g.key, { ...ctx, goal: g, tasks: data.tasks }) || { label: g.key, unit: '', kind: 'count', direction: 'up' };
    const direction = g.direction || metric.direction;
    if (g.when === 'clocked' && !worked) worked = workedDates(dept, { ...data, now });
    // a task goal closes when its task is done / removed or its date passes — read as it stood then, out of the score
    const closed = taskGoalClosed(g, data, now);
    const at = closed?.at && closed.at < now ? closed.at : now;
    const w = goalWindowFor(g, at, back, g.when === 'clocked' ? worked : null);
    const got = goalValue(g, dept, w, { ...data, now: at });
    const target = resolveTarget(g, data, got);
    const p = goalPace({ target, value: got.value, kind: metric.kind, direction, share: metric.kind === 'count' ? w.share : 1 });
    const status = w.off ? 'off' : got.feed && got.value == null ? 'feed' : p.status;
    if (w.off) p.mark = null;   // a day it doesn't count — left out of the score
    const task = isTaskGoal(g) ? { taskId: taskGoalTaskId(g), measure: g.measure, measureLabel: metric.measureLabel, taskTitle: metric.taskTitle, count: got.count || null, closed: closed ? { ...closed, met: p.status === 'met' || p.status === 'under' } : null } : null;
    return { ...g, ...task, target, needsRate: g.targetFrom?.kind === 'payRate' && myPayRate(data) == null, targetSource: targetSourceOf(g, data, got), jobTarget: got.jobTarget || null, direction, label: g.label || metric.label, unit: metric.unit, kind: metric.kind, hint: metric.hint, tappable: !!metric.tappable, window: w, ...p, status, rows: got.rows || null, feed: got.feed || null, basis: got.basis || null, notes: got.notes?.length ? got.notes : null };
  });
  const goals = all.filter((g) => !g.closed);
  const closed = all.filter((g) => g.closed).map((g) => ({ ...g, mark: null, status: 'closed' }));   // history: never deleted, never scored
  const marked = goals.filter((g) => g.mark != null);
  const score = marked.length ? Math.round(marked.reduce((a, g) => a + g.mark, 0) / marked.length) : null;
  return { departmentId: dept?.id || null, role: deptRole(dept), color: deptColor(dept), goals, closed, score, status: scoreStatus(score), unset: goals.filter((g) => g.status === 'unset').length };
}

/** The last n periods of one goal (counts and the department's own rates only — levels have no history): newest first. */
export function goalHistory(goal, dept, data = {}, n = 6, { now = data.now || Date.now() } = {}) {
  const ctx = { metrics: data.metrics || [], personalMetrics: data.personalMetrics || [] };
  const metric = goalMetric(goal.key, { ...ctx, goal, tasks: data.tasks });
  if (!metric || metric.kind === 'level' || goal.period === 'until') return [];   // an until-goal is one period: its result is the history
  const direction = goal.direction || metric.direction;
  const out = [];
  const worked = goal.when === 'clocked' ? workedDates(dept, { ...data, now }) : null;
  for (let b = 1; out.length < n && b <= n * 7; b += 1) {
    const w = goalWindow(goal.period, now, b, goal.days, worked);
    if (w.off) continue;   // a day it doesn't count
    const got = goalValue(goal, dept, w, { ...data, now });
    const p = goalPace({ target: resolveTarget(goal, data, got), value: got.value, kind: metric.kind, direction, share: 1 });
    out.push({ from: w.from, to: w.to, value: got.value, status: p.status, met: p.status === 'met' || p.status === 'under' });
  }
  return out;
}

/** "$8,400" · "94%" · "$86/h" · "3.5 h" · "41 d" · "12" */
export function formatGoalValue(v, unit = '') {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const k = (x) => Math.round(x).toLocaleString('en-CA');
  if (unit === '$') return `${n < 0 ? '−' : ''}$${k(Math.abs(n))}`;
  if (unit === '$/h') return `$${k(n)}/h`;
  if (unit === '%') return `${Math.round(n)}%`;
  if (unit === 'h') return `${round1(n)} h`;
  if (unit === 'min') return `${Math.round(n)} min`;
  if (unit === 'd') return `${Math.round(n)} d`;
  if (unit === 'wks') return `${round1(n)} wks`;
  return Number.isInteger(n) ? k(n) : String(round1(n));
}

/** The line under a goal's number: "2 short" · "+$1,950" · "limit 5 h" · "goal ≤ 30 d". */
export function goalGapText(g) {
  if (g.status === 'off') return g.when === 'clocked' ? 'counts on days you work here' : `counts ${goalDaysText(g.days)}`;
  if (g.status === 'unset') return 'no target yet';
  if (g.status === 'feed') return g.feed === 'sales' || g.feed === 'finance' ? `turns on with the books’ ${g.feed === 'sales' ? 'Sales' : 'Finance'} feed` : 'waiting on the books';
  if (g.status === 'none' || g.target == null) return '';
  if (g.kind === 'count' && g.direction === 'up' && g.status !== 'met' && g.gap != null) {
    const d = Math.abs(g.gap);
    if (d < 0.5) return 'right on pace';
    return g.gap < 0 ? `${formatGoalValue(d, g.unit)} short` : `+${formatGoalValue(d, g.unit)}`;
  }
  if (g.value == null) return '';
  const diff = Math.abs(g.value - g.target);
  if (g.direction === 'down') {
    if (g.value > g.target) return `${formatGoalValue(diff, g.unit)} over`;
    return diff > 0 ? `${formatGoalValue(diff, g.unit)} to spare` : g.target === 0 ? 'clear' : 'at the limit';
  }
  return g.value >= g.target ? 'goal met' : `${formatGoalValue(diff, g.unit)} to go`;
}

/** The tag on a goal tile: its period (counts and rates are over it); NOW for a level (it reads as it stands). */
export const goalTag = (g) => `${g.kind === 'level' ? 'NOW' : g.period === 'until' ? `DUE ${dueDateText(g.due).toUpperCase()}` : (GOAL_PERIOD_LABEL[g.period] || g.period).toUpperCase()}${g.direction === 'down' ? ' · LIMIT' : ''}`;

/** Where a project should live (General's "find it a home"): a role name, or null when General is right. */
export function homeSuggestion(project) {
  if (!project) return null;
  const n = String(project.name || '').toLowerCase();
  if (/battle ?(ops|books|bids|suite)|bmail|willow|\bapp\b|\bdev\b|software|website build/.test(n)) return 'dev';
  if (/marketing|content|youtube|reel|social|\bads?\b|brand|website/.test(n)) return 'marketing';
  if (/accounting|financ|bookkeep|tax|payroll|gst/.test(n)) return 'finance';
  if (/lead|sales|outreach|estimat|quote/.test(n)) return 'sales';
  if (num(project.value) > 0 || project.jobType || project.customerId || project.booksJobId || /reno|build|addition|stand|shed|deck|roof|paint/.test(n)) return 'field';
  return null;
}

/**
 * The dashboard's "next moves": the task linked to each goal that isn't met (behind first), then this department's
 * open tasks due today or overdue. → [{ task, why, goalId? }] at most `limit`.
 */
export function nextMoves(view, dept, data = {}, { limit = 3, date = localDateStr(data.now || Date.now()) } = {}) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const open = (t) => t && t.status !== 'done' && !t.deletedAt;
  const out = [];
  const seen = new Set();
  const goals = [...(view?.goals || [])].filter((g) => g.taskId && g.status !== 'met' && g.status !== 'under')
    .sort((a, b) => (a.mark ?? 100) - (b.mark ?? 100));
  for (const g of goals) {
    const t = byId.get(g.taskId);
    if (!open(t) || seen.has(t.id)) continue;
    seen.add(t.id);
    out.push({ task: t, goalId: g.id, why: `${g.label} · ${(STATUS_LABEL[g.status] || '').toLowerCase()}` });
  }
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const due = tasks.filter((t) => open(t) && !t.parentId && !t.bucket && !seen.has(t.id) && t.scheduledDate && t.scheduledDate <= date && wd.forTask(t)?.id === dept?.id)
    .sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)) || (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
  for (const t of due) out.push({ task: t, why: t.scheduledDate < date ? `overdue since ${t.scheduledDate}` : 'on today' });
  return out.slice(0, limit);
}

/**
 * A job's hours against its estimate (Field's "on site now"): estimate (its own tasks, parents not counted twice,
 * no checklists), hours used, the share of the estimate finished, and the pace (used ÷ (estimate × done share)).
 * → { estMin, usedMs, doneShare, pace } | null
 */
export function jobBurn(tasks = [], projectId) {
  if (!projectId) return null;
  const mine = tasks.filter((t) => t && t.projectId === projectId && !t.deletedAt && !t.bucket && !t.quickTaskId);
  const parents = new Set(mine.filter((t) => t.parentId && t.estimatedMinutes > 0).map((t) => t.parentId));
  let estMin = 0; let doneMin = 0; let usedMs = 0;
  for (const t of mine) {
    usedMs += num(t.timeSpent) || 0;
    if (parents.has(t.id) || !(t.estimatedMinutes > 0)) continue;
    estMin += t.estimatedMinutes;
    if (t.status === 'done') doneMin += t.estimatedMinutes;
  }
  if (!estMin && !usedMs) return null;
  const doneShare = estMin ? doneMin / estMin : 0;
  const earnedMs = estMin * doneShare * 60000;
  // usedShare = hours used ÷ the whole estimate — the pace line's second bar (work done vs hours used)
  return { estMin, usedMs, doneShare, usedShare: estMin ? usedMs / (estMin * 60000) : null, pace: earnedMs > 0 ? usedMs / earnedMs : null };
}

/**
 * The department's score at the end of each day this week, Monday → today (today = as it stands): the line under the
 * dial (Lj, Oct 10). Counts and rates are worked out as of that day; levels (overdue AR, oldest task…) read as they
 * stand now — there's no history kept for them. → [{ date, score, status }] (score null = no goal could be marked).
 */
export function weekScores(dept, data = {}, { now = data.now || Date.now() } = {}) {
  const today = localDateStr(now);
  const monday = goalWindow('week', now).from;
  const out = [];
  for (let d = monday; d <= today; d = addDaysStr(d, 1)) {
    const at = d === today ? now : new Date(`${d}T23:59:00`).getTime();
    const v = deptGoalsView(dept, { ...data, now: at }, { now: at });
    out.push({ date: d, score: v.score, status: v.status });
  }
  return out;
}

/** The score each day this month, the 1st → today (the hero's Month view); same rules as weekScores. */
export function monthScores(dept, data = {}, { now = data.now || Date.now() } = {}) {
  const today = localDateStr(now);
  const first = goalWindow('month', now).from;
  const out = [];
  for (let d = first; d <= today; d = addDaysStr(d, 1)) {
    const at = d === today ? now : new Date(`${d}T23:59:00`).getTime();
    out.push({ date: d, score: deptGoalsView(dept, { ...data, now: at }, { now: at }).score });
  }
  return out;
}

/** The score now against the same moment a week ago (counts as they stood then; levels as they stand). → { now, then, delta } */
export function scoreDelta(dept, data = {}, { now = data.now || Date.now() } = {}) {
  const at = now - 7 * DAY;
  const cur = deptGoalsView(dept, { ...data, now }, { now }).score;
  const then = deptGoalsView(dept, { ...data, now: at }, { now: at }).score;
  return { now: cur, then, delta: cur != null && then != null ? cur - then : null };
}

/**
 * The empty hero's one line (Lj, Oct 11: "fix the description for the score card"): what the score is and what to do,
 * named from the department's own headline presets — "Your Dev score tracks your pace on goals like shipped to live,
 * bugs closed and open bugs. Add one to start."
 */
export function scoreEmptyHint(dept) {
  const name = String(dept?.name || 'department');
  if (deptRole(dept) === 'personal') return `Your ${name} score comes from your counters. Give a counter a goal to start it.`;
  const labels = (fixedPresets(dept) || suggestedGoals(dept)).map((g) => goalMetric(g.key)?.label).filter((l) => l && l !== 'A counter').slice(0, 3).map((l) => l.toLowerCase());
  if (labels.length < 2) return `Your ${name} score tracks your pace on the goals you set. Add one to start.`;
  const list = labels.length === 3 ? `${labels[0]}, ${labels[1]} and ${labels[2]}` : `${labels[0]} and ${labels[1]}`;
  return `Your ${name} score tracks your pace on goals like ${list}. Add one to start.`;
}

/**
 * The score hero's one set of measures (phone ScoreHero + web ScoreCard draw from these, so they can't drift — Lj's
 * review, Oct 11). Sizes in px / pt; PACE_LINE = where "on pace" starts on the 0–100 dial (scoreStatus).
 */
export const SCORE_HERO = Object.freeze({
  ring: 232, ringNarrow: 168, ringEmpty: 132, outerStroke: 6, innerStroke: 12, innerInset: 16,
  headRing: 84, tileRing: 54, headStroke: 7, tileStroke: 5, radius: 22, tileRadius: 16, paceLine: 70,
  label: { onPace: 'on pace', behind: 'behind', avg: 'avg', empty: 'Build your first goal' },   // the line under it: scoreEmptyHint(dept)
});

/**
 * "All" (Lj, Oct 10: "all needs its own thing — every score and an overall average"): every department's score, and
 * the overall = the average of the business departments' scores (Personal shows but never counts — it's private and
 * never mixed with business numbers). The week line is the same average, day by day.
 * → { departments: [{ dept, view, personal }], overall, status, behind, week: [{ date, score }] }
 */
export function allScores(departments = [], data = {}, { now = data.now || Date.now(), withWeek = true } = {}) {
  const rows = departments.map((dept) => ({ dept, view: deptGoalsView(dept, data, { now }), personal: deptRole(dept) === 'personal' }));
  const avg = (list) => { const v = list.filter((x) => x != null); return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null; };
  const biz = rows.filter((r) => !r.personal);
  const overall = avg(biz.map((r) => r.view.score));
  let week = [];
  if (withWeek) {
    const per = biz.map((r) => weekScores(r.dept, data, { now }));
    week = (per[0] || weekScores({ id: '_' }, data, { now })).map((d, i) => ({ date: d.date, score: avg(per.map((p) => p[i]?.score)) }));
  }
  return { departments: rows, overall, status: scoreStatus(overall), behind: biz.filter((r) => r.view.status === 'behind').length, week };
}

/**
 * The builder's suggested target (Lj, Oct 10: "add goal > builder"): your own last four periods' average, nudged 10%
 * (up for more-is-better, down for a limit), rounded to something sayable; else the preset's starting number.
 * → { value, from: 'history' | 'preset' | null, recent: [numbers, oldest first] }
 */
export function suggestTarget(goal, dept, data = {}, { preset = null, now = data.now || Date.now() } = {}) {
  if (isTaskGoal(goal)) { const x = taskGoalSuggest(goal, { ...data, now }); return { value: x.value, from: x.value != null ? 'task' : null, recent: [], why: x.why }; }
  const ctx = { metrics: data.metrics || [], personalMetrics: data.personalMetrics || [] };
  const metric = goalMetric(goal.key, ctx);
  const presetValue = num(preset?.target ?? goal.target);
  if (!metric || metric.kind === 'level') return { value: presetValue, from: presetValue != null ? 'preset' : null, recent: [] };
  const recent = [];
  const worked = goal.when === 'clocked' ? workedDates(dept, { ...data, now }) : null;
  for (let b = 1; recent.length < 4 && b <= 28; b += 1) {
    const w = goalWindow(goal.period || 'week', now, b, goal.days, worked);
    if (!w.off) recent.unshift(goalValue({ ...goal, target: null }, dept, w, { ...data, now }).value);
  }
  const vals = recent.filter((v) => v != null && v > 0);
  if (!vals.length) return { value: presetValue, from: presetValue != null ? 'preset' : null, recent };
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const dir = goal.direction || metric.direction;
  const raw = dir === 'down' ? avg * 0.9 : avg * 1.1;
  const round = (v) => (metric.unit === '%' ? Math.min(100, Math.round(v)) : v >= 100 ? Math.round(v / 10) * 10 : v >= 10 ? Math.round(v) : Math.round(v * 10) / 10);
  return { value: round(raw), from: 'history', recent };
}
